import React, { useState, useEffect } from "react"

import { Input } from "../../../../components/shared/Inputs"
import QuillEditor, {
  BUILDER_V2_HEADER_LABELS,
  BUILDER_V2_TOOLBAR_OPTIONS
} from "../../../../components/shared/QuillEditor"
import Typography from "../../../../components/shared/Typography"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"
import BackgroundColorSelector from "../shared/BackgroundColorSelector"
import ImagePickerV2 from "../shared/ImagePickerV2"

import EditorFooter from "./shared/EditorFooter"

const ContentEditor = ({ section, practice, onSave, onCancel, onDelete }) => {
  const { handleUpdateSectionPreview, clearPreview, practice: contextPractice, pages } = useWebsiteBuilderV2()
  const practiceData = practice || contextPractice

  const [formData, setFormData] = useState({
    headline: section?.headline || "",
    subheadline: section?.subheadline || "",
    body: section?.body || "",
    imageUrl: section?.imageUrl || "",
    settings: section?.settings || {
      layout: "content_centered",
      showButton: false,
      buttonText: "Book Now",
      buttonAction: "service",
      buttonTarget: "",
      colorMode: "white",
      showBackgroundColor: false,
      backgroundColor: "#ffffff",
      highlights: [
        { title: "", description: "", imageUrl: "" },
        { title: "", description: "", imageUrl: "" },
        { title: "", description: "", imageUrl: "" }
      ],
      images: ["", "", ""] // For multi-image layouts
    }
  })
  const [errors, setErrors] = useState({})
  const [activeHighlight, setActiveHighlight] = useState(0)
  const formatServiceTargetValue = (serviceId) => `service:${serviceId}`
  const formatEventTargetValue = (eventTypeId) => `event:${eventTypeId}`
  const normalizeButtonTargetForSelect = (buttonTarget) => {
    if (!buttonTarget) return ""

    const target = String(buttonTarget)
    if (target.startsWith("service:") || target.startsWith("event:")) {
      return target
    }

    if (/^\d+$/.test(target)) {
      const eventById = (practiceData?.bookableEventTypes || []).find((eventType) => String(eventType.id) === target)
      if (eventById) return formatEventTargetValue(target)
      return formatServiceTargetValue(target)
    }

    const eventBySlug = (practiceData?.bookableEventTypes || []).find((eventType) => eventType.slug === target)
    if (eventBySlug?.id) return formatEventTargetValue(eventBySlug.id)

    return ""
  }

  useEffect(() => {
    if (section) {
      setFormData({
        headline: section.headline || "About Us",
        subheadline: section.subheadline || "",
        body: section.body || practiceData?.about || "",
        imageUrl: section.imageUrl || "",
        settings: {
          layout: "content_centered",
          showButton: false,
          buttonText: "Book Now",
          buttonAction: "service",
          buttonTarget: "",
          colorMode: "white",
          showBackgroundColor: false,
          backgroundColor: "#ffffff",
          highlights: [],
          ...section.settings
        }
      })
    }
  }, [section, practiceData])

  // Update preview when formData changes (after render)
  useEffect(() => {
    if (section?.id && formData) {
      handleUpdateSectionPreview(section.id, {
        ...section,
        ...formData
      })
    }
  }, [formData, section?.id, handleUpdateSectionPreview])

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }))
    }
  }

  const handleSettingsChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      settings: { ...prev.settings, [key]: value }
    }))
  }

  const handleHighlightChange = (index, field, value) => {
    const updatedHighlights = [...(formData.settings.highlights || [])]
    if (!updatedHighlights[index]) {
      updatedHighlights[index] = { title: "", description: "", imageUrl: "" }
    }
    updatedHighlights[index][field] = value
    handleSettingsChange("highlights", updatedHighlights)
  }

  // Close button - discard changes and close immediately
  const handleCancelClick = () => {
    clearPreview()
    onCancel()
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.headline.trim()) {
      newErrors.headline = "Section title is required"
    }

    if (formData.settings.showButton && !formData.settings.buttonText.trim()) {
      newErrors.buttonText = "Button text is required when button is enabled"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e?.preventDefault?.()
    if (validateForm()) {
      onSave({
        layout: "cta_content",
        ...formData
      })
    }
  }

  // Layout configurations - which fields to show for each layout
  const layoutConfig = {
    content_centered: { image: true, highlights: false, multipleImages: false },
    image_right: { image: true, highlights: false, multipleImages: false },
    image_left: { image: true, highlights: false, multipleImages: false },
    half_image_right: { image: true, highlights: false, multipleImages: false },
    half_image_left: { image: true, highlights: false, multipleImages: false },
    highlights_2: { image: false, highlights: 2, multipleImages: false },
    highlights_3: { image: false, highlights: 3, multipleImages: false },
    bento_left: { image: false, highlights: false, multipleImages: true },
    overlap_double_image: { image: false, highlights: false, multipleImages: true },
    text_centered: { image: false, highlights: false, multipleImages: false }
  }

  const currentConfig = layoutConfig[formData.settings.layout] || layoutConfig.content_centered

  // Layout option definitions with display info
  const layoutOptions = [
    { value: "content_centered", label: "Content Centered", icon: "📄➡️" },
    { value: "image_right", label: "Image Right", icon: "📝🖼️" },
    { value: "image_left", label: "Image Left", icon: "🖼️📝" },
    { value: "half_image_right", label: "Overlap Right", icon: "📝🔲" },
    { value: "half_image_left", label: "Overlap Left", icon: "🔲📝" },
    { value: "highlights_2", label: "2 Highlights", icon: "⭐⭐" },
    { value: "highlights_3", label: "3 Highlights", icon: "⭐⭐⭐" },
    { value: "bento_left", label: "Bento Left", icon: "🎨📝" },
    { value: "overlap_double_image", label: "Overlap Double", icon: "🔲🔲" },
    { value: "text_centered", label: "Text Centered", icon: "📄" }
  ]

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Layout Selector */}
      <div>
        <Typography variant="body" className="mb-2 font-medium text-gray-700">
          Layout
        </Typography>
        <Typography variant="small" className="mb-4 text-gray-500">
          Select a layout design for this section
        </Typography>

        <div className="grid grid-cols-4 gap-3">
          {layoutOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSettingsChange("layout", option.value)}
              className={`flex flex-col items-center justify-center rounded-lg border-2 p-3 transition-all ${
                formData.settings.layout === option.value
                  ? "border-teal-500 bg-teal-50"
                  : "border-gray-200 bg-white hover:border-gray-300"
              }`}>
              <span className="mb-1 text-2xl">{option.icon}</span>
              <span className="text-xs text-gray-600">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Section Title */}
      <div>
        <Input
          label="Section title"
          value={formData.headline}
          onChange={(e) => handleChange("headline", e.target.value)}
          placeholder="Add a short title to describe this section"
          error={errors.headline}
          required
        />
      </div>

      {/* Description - WYSIWYG Editor */}
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">Description</label>
        <Typography variant="small" className="mb-2 text-gray-500">
          Add a detailed description to provide more context.
        </Typography>
        <QuillEditor
          value={formData.body}
          onChange={(content) => handleChange("body", content)}
          placeholder="Enter your content here..."
          toolbarOptions={BUILDER_V2_TOOLBAR_OPTIONS}
          headerLabelOverrides={BUILDER_V2_HEADER_LABELS}
          className="quill-editor-v2"
        />
      </div>

      {/* Button Section */}
      <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-builder-border-light px-0 pb-6 pt-0">
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
          <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
            <p className="block leading-[24px]">Content</p>
          </div>
          <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
            <p className="block leading-[20px]">Guide visitors to the next step with a clear call-to-action button.</p>
          </div>
        </div>
        <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
          <input
            type="checkbox"
            checked={formData.settings.showButton || false}
            onChange={(e) => handleSettingsChange("showButton", e.target.checked)}
            className="relative size-6 shrink-0 accent-builder-accent"
          />
          <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-builder-primary">
            <p className="block whitespace-pre leading-[24px]">Show Button</p>
          </div>
        </div>

        {formData.settings.showButton && (
          <div className="flex w-full flex-col gap-4">
            {/* Radio buttons for button action */}
            <div className="flex items-center gap-6">
              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="radio"
                  name="buttonAction"
                  value="page"
                  checked={formData.settings.buttonAction === "page"}
                  onChange={(e) => handleSettingsChange("buttonAction", e.target.value)}
                  className="size-6 cursor-pointer accent-builder-secondary"
                />
                <span className="font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary">
                  Page
                </span>
              </label>

              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="radio"
                  name="buttonAction"
                  value="service"
                  checked={formData.settings.buttonAction === "service"}
                  onChange={(e) => handleSettingsChange("buttonAction", e.target.value)}
                  className="size-6 cursor-pointer accent-builder-secondary"
                />
                <span className="font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[24px] text-builder-primary">
                  Service or Event
                </span>
              </label>

              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="radio"
                  name="buttonAction"
                  value="external"
                  checked={formData.settings.buttonAction === "external"}
                  onChange={(e) => handleSettingsChange("buttonAction", e.target.value)}
                  className="size-6 cursor-pointer accent-builder-secondary"
                />
                <span className="font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary">
                  External Link
                </span>
              </label>
            </div>

            {/* Button text input */}
            <div className="flex flex-col gap-1">
              <label className="font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-primary">
                Button text
              </label>
              <input
                type="text"
                value={formData.settings.buttonText || "Book Now"}
                onChange={(e) => handleSettingsChange("buttonText", e.target.value)}
                placeholder="Book Now"
                className="rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] text-builder-primary focus:border-builder-accent focus:outline-none"
              />
            </div>

            {/* Page selector */}
            {formData.settings.buttonAction === "page" && (
              <div className="flex flex-col gap-1">
                <label className="font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-primary">
                  Page
                </label>
                <select
                  value={formData.settings.buttonTarget || ""}
                  onChange={(e) => handleSettingsChange("buttonTarget", e.target.value)}
                  className="rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] text-builder-primary focus:border-builder-accent focus:outline-none">
                  <option value="">Select a page</option>
                  {pages?.map((page) => (
                    <option key={page.slug} value={page.slug}>
                      {page.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Service/Event selector */}
            {formData.settings.buttonAction === "service" && (
              <div className="flex flex-col gap-1">
                <label className="font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-primary">
                  Service or Event
                </label>
                <select
                  value={normalizeButtonTargetForSelect(formData.settings.buttonTarget)}
                  onChange={(e) => handleSettingsChange("buttonTarget", e.target.value)}
                  className="rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] text-builder-primary focus:border-builder-accent focus:outline-none">
                  <option value="">Select</option>
                  <optgroup label="Services">
                    {(practiceData?.services || [])
                      .filter((s) => s.public === true && !s.package)
                      .map((service) => (
                        <option key={`service-${service.id}`} value={formatServiceTargetValue(service.id)}>
                          {service.name}
                        </option>
                      ))}
                  </optgroup>
                  <optgroup label="Packages">
                    {(practiceData?.services || [])
                      .filter((s) => s.public === true && s.package === true)
                      .map((pkg) => (
                        <option key={`package-${pkg.id}`} value={formatServiceTargetValue(pkg.id)}>
                          {pkg.name}
                        </option>
                      ))}
                  </optgroup>
                  <optgroup label="Events">
                    {(practiceData?.bookableEventTypes || []).map((eventType) => (
                      <option key={`event-type-${eventType.id}`} value={formatEventTargetValue(eventType.id)}>
                        {eventType.name}
                      </option>
                    ))}
                  </optgroup>
                </select>
                <p className="font-['Manrope:Regular',_sans-serif] text-[13px] text-builder-secondary">
                  Go to your Services to add additional options.
                </p>
              </div>
            )}

            {/* External URL input */}
            {formData.settings.buttonAction === "external" && (
              <div className="flex flex-col gap-1">
                <label className="font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-primary">
                  External URL
                </label>
                <input
                  type="url"
                  value={formData.settings.buttonTarget || ""}
                  onChange={(e) => handleSettingsChange("buttonTarget", e.target.value)}
                  placeholder="https://example.com"
                  className="rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] text-builder-primary focus:border-builder-accent focus:outline-none"
                />
                <p className="font-['Manrope:Regular',_sans-serif] text-[13px] text-builder-secondary">
                  Enter a complete URL starting with http:// or https://
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Conditional: Image Upload for single-image layouts */}
      {currentConfig.image && (
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">Image</label>
          <Typography variant="small" className="mb-2 text-gray-500">
            Select an image to feature in this section.
          </Typography>
          <ImagePickerV2
            imageUrl={formData.imageUrl}
            onImageChange={(url) => handleChange("imageUrl", url)}
            altText="Section image"
            showAltText={false}
          />
        </div>
      )}

      {/* Conditional: Multiple Images Upload */}
      {currentConfig.multipleImages && (
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">Images</label>
          <Typography variant="small" className="mb-2 text-gray-500">
            Select images to feature in this section.
          </Typography>
          <div className="space-y-4">
            {[0, 1, 2].map((index) => (
              <div key={index}>
                <label className="mb-1 block text-xs font-medium text-gray-600">Image {index + 1}</label>
                <ImagePickerV2
                  imageUrl={formData.settings.images?.[index] || ""}
                  onImageChange={(url) => {
                    const updatedImages = [...(formData.settings.images || ["", "", ""])]
                    updatedImages[index] = url
                    handleSettingsChange("images", updatedImages)
                  }}
                  altText={`Image ${index + 1}`}
                  showAltText={false}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Conditional: Highlights Section */}
      {currentConfig.highlights > 0 && (
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">Highlights</label>
          <div className="mb-4 flex gap-2">
            {Array.from({ length: currentConfig.highlights }).map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setActiveHighlight(index)}
                className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  activeHighlight === index ? "bg-teal-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}>
                Highlight {index + 1}
              </button>
            ))}
          </div>

          {/* Active Highlight Form */}
          <div className="space-y-4 rounded-lg border border-gray-200 p-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Title</label>
              <Typography variant="small" className="mb-2 text-gray-500">
                Add a short title to describe this section.
              </Typography>
              <Input
                value={formData.settings.highlights?.[activeHighlight]?.title || ""}
                onChange={(e) => handleHighlightChange(activeHighlight, "title", e.target.value)}
                placeholder="Highlight title"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Description</label>
              <Typography variant="small" className="mb-2 text-gray-500">
                Add a detailed description to provide more context.
              </Typography>
              <QuillEditor
                value={formData.settings.highlights?.[activeHighlight]?.description || ""}
                onChange={(content) => handleHighlightChange(activeHighlight, "description", content)}
                placeholder="Highlight description"
                toolbarOptions={BUILDER_V2_TOOLBAR_OPTIONS}
                headerLabelOverrides={BUILDER_V2_HEADER_LABELS}
                className="quill-editor-v2"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Image</label>
              <Typography variant="small" className="mb-2 text-gray-500">
                Select an image to feature in this section.
              </Typography>
              <ImagePickerV2
                imageUrl={formData.settings.highlights?.[activeHighlight]?.imageUrl || ""}
                onImageChange={(url) => handleHighlightChange(activeHighlight, "imageUrl", url)}
                altText="Highlight image"
                showAltText={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* Section Background Color */}
      <BackgroundColorSelector formData={formData} onSettingsChange={handleSettingsChange} sectionType="cta_content" />

      <EditorFooter
        onDelete={onDelete}
        onCancel={handleCancelClick}
        onSave={handleSubmit}
        cancelLabel="Close"
        saveLabel="Save"
      />
    </form>
  )
}

export default ContentEditor
